import { useEffect, useState } from 'react';
import { useCasbin } from '@/contexts/CasbinContext';
import { CLAIMS_LEVEL, type ClaimsLevel } from '@/modules/idv';
import { useActionGuardContainer } from './useActionGuardContainer';
import {
  useActionGuardConstraints,
  type ExtraRequiredClaimUnlock,
} from './actionGuardConstraints';

export enum UnlockFlow {
  PhoneVerificationModal = 'PhoneVerificationModal',
  EmailVerificationModal = 'EmailVerificationModal',
  ProfileWizardStart = 'ProfileWizardStart',
  ProfileWizardPhone = 'ProfileWizardPhone',
  ProfileWizardEmail = 'ProfileWizardEmail',
  ProfileWizardAttribution = 'ProfileWizardAttribution',
}

export interface HelpText {
  singleVerification: string;
  updateProfile: string;
  setupProfile: string;
}

export function extractBracketedText(text: string): string | null {
  const match = text.match(/\[([^\]]+)\]/);
  return match ? match[1] : null;
}

export function stripBracketedText(text: string): string {
  return text.replace(/\[[^\]]+\]\s*/g, '').trim();
}

export interface UseActionGuardResult {
  canPerformAction: boolean;
  isUnlockable: boolean;
  // The verification level (phone/email) that would unlock the base Casbin
  // permission, or null when none applies. The container merges this across
  // children on the verification axis.
  claimsThatWouldUnlock: ClaimsLevel | null;
  // The unmet client-side extra claim for this guard (e.g. profile attribution), or
  // null. The container merges this across children on a parallel axis.
  extraClaimUnlock: ExtraRequiredClaimUnlock | null;
  helpText: string;
  unlockFlow: UnlockFlow;
  returnFromUnlockText?: string;
  // True until the Casbin permissions cache has loaded at least once. While
  // loading, on-demand buttons should render disabled so a click cannot be
  // mistakenly intercepted into a toast before the user's real permissions
  // are known.
  isLoading: boolean;
}

/**
 * Hook to manage action guarding. It combines two gates:
 *  1. the Casbin permission for the real operation (general permissions map, or a
 *     scoped can()/could() check when `resourceId` is supplied), and
 *  2. any client-side extra required claims registered for (action, resource) via
 *     `registerExtraRequiredClaim` — e.g. "the user has set their attribution info".
 * The action is performable only when Casbin allows it AND every extra claim is
 * satisfied; it is "unlockable" when the base permission is attainable at all
 * (already allowed, or reachable via a verification upgrade).
 */
export function useActionGuard(
  action: string,
  resource: string,
  helpText: HelpText,
  resourceId?: string
): UseActionGuardResult {
  const { permissionsMap, claimUnlockingMap, isInitialized, can, could } =
    useCasbin();
  const { calculateHelpText, calculateUnlockFlow } = useActionGuardContainer();
  const { getExtraRequiredClaimUnlock } = useActionGuardConstraints();

  const scoped = resourceId !== undefined;

  const hasGeneralPermission = isInitialized
    ? (permissionsMap[action]?.includes(resource) ?? false)
    : false;

  // Scoped Casbin state: whether the caller is allowed now (can) and the minimal
  // verification level that would ever grant it (could): a ClaimsLevel — including
  // UNVERIFIED = "already allowed" — or null = "no upgrade ever grants it".
  // `undefined` means not-yet-loaded (distinct from a resolved null).
  const [scopedAllowed, setScopedAllowed] = useState<boolean | null>(null);
  const [scopedCould, setScopedCould] = useState<
    ClaimsLevel | null | undefined
  >(undefined);

  // Whenever the Casbin permission set changes (login, verification, refresh),
  // any previously-resolved scoped can()/could() answers are stale — they were
  // computed against the old server state and, on first load, possibly before
  // `hasUserId` settled (can() and could() take divergent code paths across that
  // boundary). Reset to the loading sentinels *synchronously during render* (the
  // React "adjust state when an input changes" pattern) so the guard reports
  // `isLoading` until the effect below re-resolves. Without this, a transient
  // "locked but unlockable" phantom (can() stale-false while could() already
  // resolved to UNVERIFIED) would flash the container's auto-toast on a page the
  // user can actually use.
  const [syncedPermissionsMap, setSyncedPermissionsMap] =
    useState(permissionsMap);
  if (scoped && syncedPermissionsMap !== permissionsMap) {
    setSyncedPermissionsMap(permissionsMap);
    setScopedAllowed(null);
    setScopedCould(undefined);
  }

  useEffect(() => {
    if (!scoped) {
      setScopedAllowed(null);
      setScopedCould(undefined);
      return;
    }
    let cancelled = false;
    Promise.all([
      can(action, resource, resourceId),
      could(action, resource, resourceId),
    ]).then(([allowed, couldLevel]) => {
      if (!cancelled) {
        setScopedAllowed(allowed);
        setScopedCould(couldLevel);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scoped, action, resource, resourceId, can, could, permissionsMap]);

  const generalClaim = isInitialized
    ? (claimUnlockingMap[`${action}:${resource}`] ?? null)
    : null;

  const baseAllowed = scoped ? scopedAllowed === true : hasGeneralPermission;

  // Whether the base permission is attainable at all (already allowed, or via a
  // verification upgrade). Scoped: could() returned a level (incl. UNVERIFIED),
  // not null. General: allowed now, or a claim would unlock it.
  const baseUnlockable = scoped
    ? scopedCould != null
    : hasGeneralPermission || generalClaim !== null;

  // The verification level needed when the base is not yet allowed (UNVERIFIED
  // means "already allowed", so it contributes no requirement).
  const baseClaimUnlock: ClaimsLevel | null = scoped
    ? scopedCould && scopedCould !== CLAIMS_LEVEL.UNVERIFIED
      ? scopedCould
      : null
    : generalClaim;

  const extraClaimUnlock = getExtraRequiredClaimUnlock(action, resource);

  const canPerformAction = baseAllowed && extraClaimUnlock === null;

  const isUnlockable = baseUnlockable;

  // Active unlock for THIS guard's own toast, base-claim before extra-claim (the
  // base permission must be satisfied before an extra claim on top of it matters).
  let unlockFlow: UnlockFlow;
  let selectedHelpText: string;
  let returnFromUnlockText: string | undefined;
  if (!baseAllowed && baseClaimUnlock !== null) {
    unlockFlow = calculateUnlockFlow(baseClaimUnlock);
    selectedHelpText = calculateHelpText(baseClaimUnlock, helpText);
    returnFromUnlockText = undefined;
  } else if (extraClaimUnlock) {
    unlockFlow = extraClaimUnlock.unlockFlow;
    selectedHelpText = extraClaimUnlock.helpText;
    returnFromUnlockText = extraClaimUnlock.returnFromUnlockText;
  } else {
    unlockFlow = calculateUnlockFlow(baseClaimUnlock);
    selectedHelpText = calculateHelpText(baseClaimUnlock, helpText);
    returnFromUnlockText = undefined;
  }

  // Pre-init (permissions not loaded at all) the scoped answers can't yet be
  // trusted — could() may resolve against a JWT while can() falls back to an
  // empty general map. Treat that window, and any unresolved scoped promise, as
  // loading.
  const isLoading = scoped
    ? !isInitialized || scopedAllowed === null || scopedCould === undefined
    : !isInitialized;

  return {
    canPerformAction,
    isUnlockable,
    claimsThatWouldUnlock: baseClaimUnlock,
    extraClaimUnlock,
    helpText: selectedHelpText,
    unlockFlow,
    returnFromUnlockText,
    isLoading,
  };
}
